import { useState } from "react";
import { InputField } from "@jotrseatrc/shared-components";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faMinusCircle,
  faPersonRunning,
  faChalkboardTeacher,
} from "@fortawesome/free-solid-svg-icons";

export const InputFieldActivity = ({
  mode,
  formData,
  categoryFormData,
  newRundown,
  editingRundownIndex,
  handleChange,
  handleTypeChange,
  handleImageChange,
  handleCategoryChange,
  handleGPXChange,
  handleElevationImageChange,
  handleRundownChange,
  handleAddRundown,
  handleEditRundown,
  handleSaveRundown,
  handleRemoveRundown,
  handleAddCategory,
  handleEditCategory,
  handleRemoveCategory,
  isFormDisabled,
  labelColor,
  currentImageUrl,
  currentGpxFileUrl,
  currentElevationImageUrl,
}) => {
  return (
    <div
      className={
        mode === "editCategory"
          ? "space-y-4"
          : "grid grid-cols-1 md:grid-cols-2 gap-4"
      }
    >
      {(mode === "add" || mode === "edit") && (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-800">
            Activity Information
          </h4>
          <InputField
            label="Activity Title"
            name="title"
            type="text"
            value={formData.title || ""}
            onChange={handleChange}
            placeholder="Enter activity title"
            required
            disabled={isFormDisabled}
            labelColor={labelColor}
          />
          <InputField
            label={`Activity Image${
              mode === "edit"
                ? " (Leave blank if you don't want to change)"
                : ""
            }`}
            name="image"
            type="file"
            onChange={handleImageChange}
            accept="image/*"
            required={mode === "add"}
            disabled={isFormDisabled}
            labelColor={labelColor}
          />
          {currentImageUrl && mode === "edit" && (
            <p className="mt-2 text-sm text-gray-500">
              Current image:{" "}
              <a
                href={currentImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:underline"
              >
                View
              </a>
            </p>
          )}
          <div className="mb-4">
            <label className={`block text-sm font-medium ${labelColor} mb-2`}>
              Activity Type
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleTypeChange("trail_running")}
                className={`rounded-xl flex items-center justify-center gap-3 px-3 py-2 border ${
                  formData.type === "trail_running"
                    ? "border-cyan-600 bg-cyan-100 text-cyan-600"
                    : "border-gray-300 text-gray-500 hover:border-gray-400"
                }`}
                disabled={isFormDisabled}
              >
                <FontAwesomeIcon icon={faPersonRunning} />
                <span>Trail Running Race</span>
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange("seminar")}
                className={`rounded-xl flex items-center justify-center gap-3 px-3 py-2 border ${
                  formData.type === "seminar"
                    ? "border-indigo-600 bg-indigo-100 text-indigo-600"
                    : "border-gray-300 text-gray-500 hover:border-gray-400"
                }`}
                disabled={isFormDisabled}
              >
                <FontAwesomeIcon icon={faChalkboardTeacher} />
                <span>Seminar</span>
              </button>
            </div>
          </div>
          <InputField
            label="Location"
            name="location"
            type="text"
            value={formData.location || ""}
            onChange={handleChange}
            placeholder="Enter activity location"
            required
            disabled={isFormDisabled}
            labelColor={labelColor}
          />
          <InputField
            label="Status"
            name="status"
            type="select"
            value={formData.status || ""}
            onChange={handleChange}
            options={[
              { value: "Segera Dibuka", label: "Coming Soon" },
              { value: "Registrasi Dibuka", label: "Registration Open" },
              { value: "Registrasi Ditutup", label: "Registration Closed" },
            ]}
            defaultOptionLabel="Select status..."
            disabled={isFormDisabled}
            labelColor={labelColor}
          />
          <InputField
            label="Event ID"
            name="eventId"
            type="text"
            value={formData.eventId || ""}
            onChange={handleChange}
            placeholder="Enter event ID"
            required
            disabled={isFormDisabled}
            labelColor={labelColor}
          />
          <InputField
            label="Slug"
            name="slug"
            type="text"
            value={formData.slug || ""}
            onChange={handleChange}
            placeholder="Enter slug (unique)"
            required
            disabled={isFormDisabled}
            labelColor={labelColor}
          />
          <InputField
            label="Activity Website (optional)"
            name="websiteUrl"
            type="url"
            value={formData.websiteUrl || ""}
            onChange={handleChange}
            placeholder="Enter website URL (e.g., https://example.com)"
            disabled={isFormDisabled}
            labelColor={labelColor}
          />
          <InputField
            label="About Activity"
            name="aboutEvent"
            type="textarea"
            value={formData.aboutEvent || ""}
            onChange={handleChange}
            placeholder="Enter description about activity"
            required
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="resize-y h-40 min-h-[10rem]"
          />
          {formData.type === "seminar" && (
            <div className="space-y-4">
              <InputField
                label="Description"
                name="description"
                type="textarea"
                value={formData.categories[0]?.description || ""}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "description",
                      value: e.target.value,
                      isSeminar: true,
                    },
                  })
                }
                placeholder="Enter activity description"
                required
                disabled={isFormDisabled}
                labelColor={labelColor}
                className="resize-y h-40 min-h-[10rem]"
              />
              <InputField
                label="Date"
                name="date"
                type="date"
                value={formData.categories[0]?.date || ""}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "date",
                      value: e.target.value,
                      isSeminar: true,
                    },
                  })
                }
                required
                disabled={isFormDisabled}
                labelColor={labelColor}
              />
              <InputField
                label="Time"
                name="time"
                type="time"
                value={formData.categories[0]?.time || ""}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "time",
                      value: e.target.value,
                      isSeminar: true,
                    },
                  })
                }
                required
                disabled={isFormDisabled}
                labelColor={labelColor}
              />
              <InputField
                label="Price"
                name="price"
                type="text"
                value={formData.categories[0]?.price || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleChange({
                      target: {
                        name: "price",
                        value: value || 0,
                        isSeminar: true,
                      },
                    });
                  }
                }}
                placeholder="Enter price"
                required
                disabled={isFormDisabled}
                labelColor={labelColor}
              />
            </div>
          )}
        </div>
      )}

      {(mode === "add" || mode === "edit") &&
        formData.type === "trail_running" && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-800">
              Add Category
            </h4>
            <InputField
              label="Distance Category Name"
              name="categoryName"
              type="text"
              value={categoryFormData.categoryName || ""}
              onChange={handleCategoryChange}
              placeholder="Enter category name (e.g., Classic 20K)"
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="Distance (km)"
              name="distance"
              type="text"
              value={categoryFormData.distance || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value))
                  handleCategoryChange({ target: { name: "distance", value } });
              }}
              placeholder="Enter distance in km"
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="Elevation Gain (m)"
              name="elevationGain"
              type="text"
              value={categoryFormData.elevationGain || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value))
                  handleCategoryChange({
                    target: { name: "elevationGain", value },
                  });
              }}
              placeholder="Enter elevation gain in meters"
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="Date"
              name="date"
              type="date"
              value={categoryFormData.date || ""}
              onChange={handleCategoryChange}
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="Time"
              name="time"
              type="time"
              value={categoryFormData.time || ""}
              onChange={handleCategoryChange}
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="COT (hours)"
              name="cot"
              type="text"
              value={categoryFormData.cot || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value))
                  handleCategoryChange({ target: { name: "cot", value } });
              }}
              placeholder="Enter cut-off time in hours"
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="Price (Rp)"
              name="price"
              type="text"
              value={categoryFormData.price || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value))
                  handleCategoryChange({ target: { name: "price", value } });
              }}
              placeholder="Enter category price"
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="Category Description"
              name="description"
              type="textarea"
              value={categoryFormData.description || ""}
              onChange={handleCategoryChange}
              placeholder="Enter category description"
              disabled={isFormDisabled}
              labelColor={labelColor}
              className="resize-y h-40 min-h-[10rem]"
            />
            <InputField
              label="Iframe Map URL (optional)"
              name="mapUrl"
              type="url"
              value={categoryFormData.mapUrl || ""}
              onChange={handleCategoryChange}
              placeholder="Enter map iframe URL (e.g., https://www.google.com/maps/embed/...)"
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="GPX File (optional)"
              name="gpxFile"
              type="file"
              onChange={handleGPXChange}
              accept=".gpx"
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <InputField
              label="Elevation Image (optional)"
              name="imageFile"
              type="file"
              onChange={handleElevationImageChange}
              accept="image/jpeg,image/png"
              disabled={isFormDisabled}
              labelColor={labelColor}
            />
            <div className="space-y-2">
              <h5 className="text-md font-semibold text-gray-800">Rundown</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <InputField
                  label="Activity"
                  name="activity"
                  type="text"
                  value={newRundown.activity || ""}
                  onChange={handleRundownChange}
                  placeholder="Enter activity name"
                  disabled={isFormDisabled}
                  labelColor={labelColor}
                />
                <InputField
                  label="Date"
                  name="date"
                  type="date"
                  value={newRundown.date || ""}
                  onChange={handleRundownChange}
                  disabled={isFormDisabled}
                  labelColor={labelColor}
                />
                <InputField
                  label="Time"
                  name="time"
                  type="time"
                  value={newRundown.time || ""}
                  onChange={handleRundownChange}
                  disabled={isFormDisabled}
                  labelColor={labelColor}
                />
                <InputField
                  label="Location"
                  name="location"
                  type="text"
                  value={newRundown.location || ""}
                  onChange={handleRundownChange}
                  placeholder="Enter activity location"
                  disabled={isFormDisabled}
                  labelColor={labelColor}
                />
              </div>
              {editingRundownIndex === null ? (
                <button
                  type="button"
                  onClick={() => handleAddRundown(false)}
                  className="w-full px-4 py-3 mt-4 bg-cyan-600 text-white rounded-xl hover:bg-cyan-800"
                  disabled={isFormDisabled}
                >
                  Add Rundown
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSaveRundown(false)}
                    className="w-full px-4 py-3 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700"
                    disabled={isFormDisabled}
                  >
                    Save Rundown
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleRundownChange({
                        target: { name: "activity", value: "" },
                      });
                      handleRundownChange({
                        target: { name: "date", value: "" },
                      });
                      handleRundownChange({
                        target: { name: "time", value: "" },
                      });
                      handleRundownChange({
                        target: { name: "location", value: "" },
                      });
                      handleEditRundown(null);
                    }}
                    className="w-full px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400"
                    disabled={isFormDisabled}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {categoryFormData.rundown &&
                categoryFormData.rundown.length > 0 && (
                  <div className="mt-2">
                    <ul className="space-y-2 max-h-32 overflow-y-auto">
                      {categoryFormData.rundown.map((item, index) => (
                        <li
                          key={index}
                          className="flex justify-between items-center p-2 bg-gray-100 rounded-xl"
                        >
                          <span>{`${item.activity} - ${item.date} ${item.time} @ ${item.location}`}</span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditRundown(index, false)}
                              className="text-indigo-600 hover:text-indigo-900"
                              disabled={isFormDisabled}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveRundown(index, false)}
                              className="text-red-600 hover:text-red-900"
                              disabled={isFormDisabled}
                            >
                              <FontAwesomeIcon icon={faMinusCircle} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
            <button
              type="button"
              onClick={() => handleAddCategory(mode === "edit")}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700"
              disabled={isFormDisabled}
            >
              Add Category
            </button>
            {formData.categories && formData.categories.length > 0 && (
              <div className="mt-4">
                <h5 className="text-md font-semibold text-gray-800 mb-2">
                  Category List
                </h5>
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.categories.map((category, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center p-4 bg-gray-100 rounded-xl"
                    >
                      <span>{`${
                        category.categoryName || `${category.distance} km`
                      } - ${category.description} (Rp ${(
                        category.price || 0
                      ).toLocaleString("id-ID")})`}</span>
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={() => handleEditCategory(index)}
                          className="text-indigo-600 hover:text-indigo-900"
                          disabled={isFormDisabled}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveCategory(index, mode === "edit")
                          }
                          className="text-red-600 hover:text-red-900"
                          disabled={isFormDisabled}
                        >
                          <FontAwesomeIcon icon={faMinusCircle} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

      {mode === "editCategory" && (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-800">Edit Category</h4>
          <InputField
            label="Distance Category Name"
            name="categoryName"
            type="text"
            value={formData.categoryName || ""}
            onChange={handleCategoryChange}
            placeholder="Enter category name (e.g., Classic 20K)"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          <InputField
            label="Distance (km)"
            name="distance"
            type="text"
            value={formData.distance || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value))
                handleCategoryChange({ target: { name: "distance", value } });
            }}
            placeholder="Enter distance in km"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          <InputField
            label="Elevation Gain (m)"
            name="elevationGain"
            type="text"
            value={formData.elevationGain || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value))
                handleCategoryChange({
                  target: { name: "elevationGain", value },
                });
            }}
            placeholder="Enter elevation gain in meters"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          <InputField
            label="Date"
            name="date"
            type="date"
            value={formData.date || ""}
            onChange={handleCategoryChange}
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          <InputField
            label="Time"
            name="time"
            type="time"
            value={formData.time || ""}
            onChange={handleCategoryChange}
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          <InputField
            label="COT (hours)"
            name="cot"
            type="text"
            value={formData.cot || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value))
                handleCategoryChange({ target: { name: "cot", value } });
            }}
            placeholder="Masukkan cut-off time dalam jam"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          <InputField
            label="Price (Rp)"
            name="price"
            type="text"
            value={formData.price || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value))
                handleCategoryChange({ target: { name: "price", value } });
            }}
            placeholder="Enter category price"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          <InputField
            label="Category Description"
            name="description"
            type="textarea"
            value={formData.description || ""}
            onChange={handleCategoryChange}
            placeholder="Enter category description"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="resize-y h-40 min-h-[10rem] w-full"
          />
          <InputField
            label="Iframe Map URL (optional)"
            name="mapUrl"
            type="url"
            value={formData.mapUrl || ""}
            onChange={handleCategoryChange}
            placeholder="Enter map iframe URL (e.g., https://www.google.com/maps/embed/...)"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          <InputField
            label="GPX File (Leave blank if you don't want to change)"
            name="gpxFile"
            type="file"
            onChange={handleGPXChange}
            accept=".gpx"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          {currentGpxFileUrl && (
            <p className="mt-2 text-sm text-gray-500">
              Current GPX file:{" "}
              <a
                href={currentGpxFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:underline"
              >
                View
              </a>
            </p>
          )}
          <InputField
            label="Elevation Image (Leave blank if you don't want to change)"
            name="imageFile"
            type="file"
            onChange={handleElevationImageChange}
            accept="image/jpeg,image/png"
            disabled={isFormDisabled}
            labelColor={labelColor}
            className="w-full"
          />
          {currentElevationImageUrl && (
            <p className="mt-2 text-sm text-gray-500">
              Current Elevation Image:{" "}
              <a
                href={currentElevationImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:underline"
              >
                View
              </a>
            </p>
          )}
          <div className="space-y-2">
            <h5 className="text-md font-semibold text-gray-800">Rundown</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <InputField
                label="Activity"
                name="activity"
                type="text"
                value={newRundown.activity || ""}
                onChange={handleRundownChange}
                placeholder="Enter activity name"
                disabled={isFormDisabled}
                labelColor={labelColor}
                className="w-full"
              />
              <InputField
                label="Date"
                name="date"
                type="date"
                value={newRundown.date || ""}
                onChange={handleRundownChange}
                disabled={isFormDisabled}
                labelColor={labelColor}
                className="w-full"
              />
              <InputField
                label="Time"
                name="time"
                type="time"
                value={newRundown.time || ""}
                onChange={handleRundownChange}
                disabled={isFormDisabled}
                labelColor={labelColor}
                className="w-full"
              />
              <InputField
                label="Location"
                name="location"
                type="text"
                value={newRundown.location || ""}
                onChange={handleRundownChange}
                placeholder="Enter activity location"
                disabled={isFormDisabled}
                labelColor={labelColor}
                className="w-full"
              />
            </div>
            {editingRundownIndex === null ? (
              <button
                type="button"
                onClick={() => handleAddRundown(true)}
                className="w-full px-4 py-3 mt-4 bg-cyan-600 text-white rounded-xl hover:bg-cyan-800"
                disabled={isFormDisabled}
              >
                Add Rundown
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleSaveRundown(true)}
                  className="w-full px-4 py-3 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700"
                  disabled={isFormDisabled}
                >
                  Simpan Rundown
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleRundownChange({
                      target: { name: "activity", value: "" },
                    });
                    handleRundownChange({
                      target: { name: "date", value: "" },
                    });
                    handleRundownChange({
                      target: { name: "time", value: "" },
                    });
                    handleRundownChange({
                      target: { name: "location", value: "" },
                    });
                    handleEditRundown(null);
                  }}
                  className="w-full px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400"
                  disabled={isFormDisabled}
                >
                  Batal
                </button>
              </div>
            )}
            {formData.rundown && formData.rundown.length > 0 && (
              <div className="mt-2">
                <ul className="space-y-2 max-h-32 overflow-y-auto w-full">
                  {formData.rundown.map((item, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center p-2 bg-gray-100 rounded-xl"
                    >
                      <span>{`${item.activity} - ${item.date} ${item.time} @ ${item.location}`}</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditRundown(index, true)}
                          className="text-indigo-600 hover:text-indigo-900"
                          disabled={isFormDisabled}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveRundown(index, true)}
                          className="text-red-600 hover:text-red-900"
                          disabled={isFormDisabled}
                        >
                          <FontAwesomeIcon icon={faMinusCircle} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

InputFieldActivity.propTypes = {
  mode: PropTypes.oneOf(["add", "edit", "editCategory"]).isRequired,
  formData: PropTypes.object.isRequired,
  categoryFormData: PropTypes.object.isRequired,
  newRundown: PropTypes.object.isRequired,
  editingRundownIndex: PropTypes.number,
  handleChange: PropTypes.func.isRequired,
  handleTypeChange: PropTypes.func.isRequired,
  handleImageChange: PropTypes.func.isRequired,
  handleCategoryChange: PropTypes.func.isRequired,
  handleGPXChange: PropTypes.func.isRequired,
  handleElevationImageChange: PropTypes.func.isRequired,
  handleRundownChange: PropTypes.func.isRequired,
  handleAddRundown: PropTypes.func.isRequired,
  handleEditRundown: PropTypes.func.isRequired,
  handleSaveRundown: PropTypes.func.isRequired,
  handleRemoveRundown: PropTypes.func.isRequired,
  handleAddCategory: PropTypes.func,
  handleEditCategory: PropTypes.func,
  handleRemoveCategory: PropTypes.func,
  isFormDisabled: PropTypes.bool,
  labelColor: PropTypes.string,
  currentImageUrl: PropTypes.string,
  currentGpxFileUrl: PropTypes.string,
  currentElevationImageUrl: PropTypes.string,
};
